/* eslint-disable react/jsx-no-useless-fragment */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import { ROUTE_PATH } from 'routes';
import { useNavigate, useParams } from 'react-router-dom';
import useLiterals from 'utils/hooks/useLiterals';
import {
  createBasicSignup,
  generateSessionToken,
  getUserOtp,
  onLogin,
  postCompleteSignup,
  savePassword,
  searchClient,
  sendSignupOTP,
  verifyClientAccountOTP,
} from 'modules/session/actions';
import useLocalStorage from 'utils/hooks/useLocalStorage';
import { CLIENT_EXISTS_TYPES, STEPS, SUCCESS_TYPES } from 'constants/registration';
import { STORAGE_USER_DATA } from 'constants/local';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import { LOGIN_TYPES, PHONE_COUNTRY_CODE, USER_LEVEL } from 'constants/global';
import Button from 'components/UI/Button';
import useFeatureGate from 'utils/hooks/useFeatureGate';
import { FEATURE_REGISTRATION, FEATURE_REGISTRATION_NEW_CLIENT } from 'constants/featureGates';
import { sendGaEvent } from 'utils/sendGaEvent';
import { GA_EVENTS, GA_REGISTRATION_ACTIONS, GA_REGISTRATION_LABELS } from 'constants/gaEvents';
import { sendSentryLog } from 'utils/sendSentryLog';
import ClientBasicInfo from './components/ClientBasicInfo';
import ClientOTPValidation from './components/ClientOTPValidation';
import ClientPassword from './components/ClientPassword';
import ClientIdentityValidation from './components/ClientIdentityValidation';
import ClientIdentityResume from './components/ClientIdentityResume';
import ClientAddress from './components/ClientAddress';
import ClientSuccessMessage from './components/ClientSuccessMessage';
import ClientStart from './components/ClientStart';
import ClientExistsOTPValidation from './components/ClientExistsOTPValidation';
import './styles.scss';

const ERROR_MESSAGES = {
  USER_ALREADY_EXISTS: 'USER_ALREADY_EXISTS',
  PHONE_ALREADY_EXISTS: 'PHONE_ALREADY_EXISTS',
  PHONE_ALREADY_EXISTS_IN_SAP: 'PHONE_ALREADY_EXISTS_IN_SAP',
  EMAIL_ALREADY_EXISTS: 'EMAIL_ALREADY_EXISTS',
  EMAIL_ALREADY_EXISTS_IN_SAP: 'EMAIL_ALREADY_EXISTS_IN_SAP',
  RFC_ALREADY_EXISTS: 'RFC_ALREADY_EXISTS',
  ERROR_CHECKING_EMAIL: 'ERROR_CHECKING_EMAIL',
  INVALID_PASSWORD_TOKEN: 'INVALID_PASSWORD_TOKEN',
};

const Registration = () => {
  const { step } = useParams();
  const navigate = useNavigate();
  const literals = useLiterals('registration');
  const { user: sessionUser } = useSelector((state) => state.session);

  // Registration is available on every platform, but on desktop web only the
  // existing-client linking path is offered; new-client signup + KYC stays
  // mobile-only (WEB_MLS-608).
  const registrationEnabled = useFeatureGate(FEATURE_REGISTRATION);
  const newClientEnabled = useFeatureGate(FEATURE_REGISTRATION_NEW_CLIENT);
  useEffect(() => {
    if (!registrationEnabled) {
      navigate(ROUTE_PATH.HOME, { replace: true });
    }
  }, [registrationEnabled, navigate]);

  // Steps that belong to the new-client signup + KYC flow; unreachable when
  // that flow is disabled (desktop web).
  useEffect(() => {
    const newClientOnlySteps = [
      STEPS.BASIC_INFO,
      STEPS.OTP_VALIDATION,
      STEPS.IDENTITY_VALIDATION,
      STEPS.IDENTITY_RESUME,
      STEPS.ADDRESS,
    ];
    if (!newClientEnabled && newClientOnlySteps.includes(step)) {
      navigate(
        sessionUser?.id ? ROUTE_PATH.HOME : ROUTE_PATH.setRegistration(STEPS.START),
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, newClientEnabled]);

  const [error, setError] = useState(null);
  const [exists, setExists] = useState({ type: CLIENT_EXISTS_TYPES.CLIENT_ID, value: '', linked: false });
  const [errorEmailPassword, setErrorEmailPassword] = useState(false);
  const [user, setUser] = useState({
    firstName: '',
    lastName: '',
    paternal: '',
    maternal: '',
    gender: '',
    email: '',
    phone: '',
    privacyTerms: false,
    appTerms: false,
  });
  const [userExtra, setUserExtra] = useState({
    job: '',
    referral: '',
    educationLevel: '',
    countryOfBirth: '',
    citizenship: '',
  });
  const [passwordToken, setPasswordToken] = useState(null);
  const [userData, setUserData] = useLocalStorage(STORAGE_USER_DATA);
  const dispatch = useDispatch();

  const handleEvent = (type, label = '', nonInteraction = true) => {
    let auxLabel = label;
    if (typeof label === 'object') {
      if (typeof label?.data?.error === 'string') {
        auxLabel = label?.data?.error;
      } else {
        auxLabel = JSON.stringify(label).slice(0, 90);
      }
    }
    sendGaEvent({
      ...GA_EVENTS.REGISTRATION,
      action: `${step} | ${type}`,
      label: auxLabel,
      nonInteraction,
    });
  };

  useEffect(() => {
    if (sessionUser?.id) {
      if (sessionUser?.level === USER_LEVEL.COMPLETE || !newClientEnabled) {
        navigate(ROUTE_PATH.HOME, { replace: true });
      } else if (step !== STEPS.IDENTITY_VALIDATION) {
        navigate(ROUTE_PATH.setRegistration(STEPS.IDENTITY_VALIDATION), { replace: true });
      }
    } else if (!Object.values(STEPS).includes(step) || (step !== STEPS.START && !user?.phone)) {
      if (step === STEPS.PASSWORD && window.mlsQueryParams?.token) {
        setPasswordToken(window.mlsQueryParams?.token);
      } else {
        navigate(ROUTE_PATH.setRegistration(STEPS.START), { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleEvent(GA_REGISTRATION_ACTIONS.START);
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleBack = () => {
    switch (step) {
      case STEPS.START:
      case STEPS.BASIC_INFO:
      case STEPS.OTP_VALIDATION:
      case STEPS.EXISTS_OTP_VALIDATION:
      case STEPS.IDENTITY_RESUME:
      case STEPS.ADDRESS:
        navigate(-1);
        break;
      default:
        break;
    }
  };

  const handleContinue = (data) => {
    const onContinue = () => {
      handleEvent(GA_REGISTRATION_ACTIONS.COMPLETE);
    };

    const onError = (e) => {
      handleEvent(GA_REGISTRATION_ACTIONS.ERROR, e);
      let auxError = e;
      try {
        const errorString = JSON.stringify(e);
        let overwriteError = null;
        if (errorString.includes('Celular ya existe')) {
          overwriteError = ERROR_MESSAGES.PHONE_ALREADY_EXISTS_IN_SAP;
        } else if (errorString.includes('Correo ya existe')) {
          overwriteError = ERROR_MESSAGES.EMAIL_ALREADY_EXISTS_IN_SAP;
        } else if (errorString.includes('El RFC a registrar esta asociado a otro BP')) {
          overwriteError = ERROR_MESSAGES.RFC_ALREADY_EXISTS;
        }
        if (overwriteError) {
          auxError = { data: { error: overwriteError } };
        }
      } catch (ev) {
        /* */
      }
      setError(auxError);
    };

    switch (step) {
      case STEPS.START:
        if (data) {
          handleEvent(
            GA_REGISTRATION_ACTIONS.EVENT,
            exists.type === CLIENT_EXISTS_TYPES.INE
              ? GA_REGISTRATION_LABELS.START_SUBMIT_INE
              : GA_REGISTRATION_LABELS.START_SUBMIT_CLIENT_ID,
          );
          dispatch(searchClient(exists.type, exists.value)).then((response) => {
            onContinue();
            setExists((prev) => ({
              ...prev,
              email: response?.email || '',
              phone: response?.phone || '',
            }));
            navigate(ROUTE_PATH.setRegistration(STEPS.EXISTS_OTP_VALIDATION));
          }).catch((e) => {
            switch (e.statusCode) {
              case 406:
                e.data.error = ERROR_MESSAGES.USER_ALREADY_EXISTS;
                break;
              case 409:
                e.data.error = literals.start.errors.incompleteData;
                break;
              case 500:
                e.data.error = literals.start.errors.serviceUnavailable;
                break;
              default:
                e.data.error = literals.start.errors.userNotFound;
            }
            onError(e);
          });
        } else {
          onContinue();
          setExists((prev) => ({
            ...prev,
            email: '',
            phone: '',
          }));
          navigate(ROUTE_PATH.setRegistration(STEPS.BASIC_INFO));
        }
        break;
      case STEPS.BASIC_INFO:
        dispatch(sendSignupOTP(user.email, `${PHONE_COUNTRY_CODE}${user.phone}`)).then(() => {
          onContinue();
          navigate(ROUTE_PATH.setRegistration(STEPS.OTP_VALIDATION));
        }).catch((e) => {
          if (typeof e?.data?.error === 'string' && literals.basicInfo?.errors?.[e?.data?.error]) {
            e.data.error = literals.basicInfo.errors[e?.data?.error];
          }
          onError(e);
          sendSentryLog({
            error: e,
            data: {
              n: `${user.firstName} ${user.paternal}`,
              e: user.email,
              p: user.phone,
            },
          }, 'Onboarding: basic signup send otp error');
        });
        break;
      case STEPS.EXISTS_OTP_VALIDATION:
        dispatch(verifyClientAccountOTP(exists.value, data)).then((response) => {
          setPasswordToken(response.token);
          setUser((prev) => ({
            ...prev,
            clientId: response?.client_id || response?.clientId || '',
          }));
          setExists((prev) => ({
            ...prev,
            linked: true,
          }));
          onContinue();
          navigate(ROUTE_PATH.setRegistration(STEPS.PASSWORD), { replace: true });
        }).catch((e) => {
          try {
            if (e?.statusCode === 401) {
              e.data.error = literals.existsOTPValidation.errors.invalidOTP;
            }
          } catch (ev) {
            /* */
          }
          sendSentryLog({
            error: e,
            data: { ...exists },
          }, 'Onboarding: Exists otp validation error');
          onError(e);
        });
        break;
      case STEPS.OTP_VALIDATION:
        dispatch(createBasicSignup({ ...user, phone: `${PHONE_COUNTRY_CODE}${user.phone}` }, data)).then((response) => {
          setPasswordToken(response.token);
          onContinue();
          navigate(ROUTE_PATH.setRegistration(STEPS.PASSWORD), { replace: true });
        }).catch((e) => {
          sendSentryLog({
            error: e,
            data: {
              n: `${user.firstName} ${user.paternal}`,
              e: user.email,
              p: user.phone,
            },
          }, 'Onboarding: basic signup error');
          onError(e);
        });
        break;
      case STEPS.PASSWORD: {
        // Cuando viene desde el link de correo electrónico de continuar registro no tenemos los pasos anteriores
        const fromEmailLink = !user.email;

        const onSavePassword = () => {
          onContinue();
          if (!fromEmailLink) {
            navigate(ROUTE_PATH.setRegistration(exists?.linked ? STEPS.SUCCESS_BASIC_LINKED : STEPS.SUCCESS_BASIC), { replace: true });
          }
          const loginData = {
            email: user.email || data.email || '',
            idClient: user.clientId || '',
            password: data.password,
            signInType: user.clientId ? LOGIN_TYPES.CLIENT_ID : LOGIN_TYPES.EMAIL,
            welcome_email: true,
          };
          dispatch(onLogin(loginData)).then((response) => {
            if (response) {
              setUserData({
                ...userData,
                id: response?.id,
                lastUserName: response?.firstName || '',
                lastLogin: new Date().getTime(),
                email: response?.email,
                clientId: response?.clientId,
              });
              setUser({
                ...user,
                firstName: user.firstName || response.firstName || '',
                lastName: user.lastName || response.lastName || '',
                email: user.email || data.email || '',
                phone: user.phone || response.phone || '',
                clientId: response?.clientId || '-',
              });
              if (fromEmailLink) {
                navigate(ROUTE_PATH.setRegistration(STEPS.SUCCESS_BASIC), { replace: true });
              }
              if (data.biometric) {
                generateSessionToken({ id: response.id }, (success) => {
                  if (success) {
                    setUserData((prev) => ({ ...prev, biometric: true }));
                  }
                });
              }
            } else if (fromEmailLink) {
              setErrorEmailPassword(true);
              onError({ data: { error: literals.password.passwordSavedButInvalidEmail } });
            }
          });
        };

        if (errorEmailPassword) {
          onSavePassword();
        } else {
          dispatch(savePassword(passwordToken, data.password))
            .then(onSavePassword)
            .catch((e) => {
              if (fromEmailLink && e.statusCode === 401) {
                e.data.error = ERROR_MESSAGES.INVALID_PASSWORD_TOKEN;
              }
              onError(e);
            });
        }
        break;
      }
      case STEPS.SUCCESS_BASIC:
      case STEPS.SUCCESS_BASIC_LINKED:
        onContinue();
        if (newClientEnabled) {
          navigate(ROUTE_PATH.setRegistration(STEPS.IDENTITY_VALIDATION));
        } else {
          navigate(ROUTE_PATH.HOME, { replace: true });
        }
        break;
      case STEPS.IDENTITY_VALIDATION:
        setUserExtra({
          ...userExtra,
          ...data,
        });
        onContinue();
        navigate(ROUTE_PATH.setRegistration(STEPS.IDENTITY_RESUME));
        break;
      case STEPS.IDENTITY_RESUME:
        onContinue();
        navigate(ROUTE_PATH.setRegistration(STEPS.ADDRESS));
        break;
      case STEPS.ADDRESS:
        dispatch(postCompleteSignup({
          id_mission: {
            session_id: userExtra.idMission?.sessionId || '',
            request_id: userExtra.idMission?.requestId || '',
            unique_request_id: userExtra.idMission?.uniqueRequestId || '',
            verification_result_id: userExtra.idMission?.verificationResultId || '',
          },
          kyc: {
            job: userExtra.job,
            referral: userExtra.referral,
            education_level: userExtra.educationLevel,
          },
          address: {
            country: 'MX',
            postal_code: data.postalCode,
            neighborhood: data.neighborhood,
            state: data.state,
            municipality: data.city,
            street: data.street,
            exterior_number: data.number,
            interior_number: data.internalNumber,
            prof_of_address: data.document,
          },
          country_of_birth: userExtra.countryOfBirth,
          citizenship: userExtra.citizenship,
        })).then(() => {
          onContinue();
          navigate(ROUTE_PATH.setRegistration(STEPS.SUCCESS_COMPLETE), { replace: true });
        }).catch((e) => {
          sendSentryLog({
            error: e,
            data: {
              u: sessionUser?.id,
              c: sessionUser?.clientId,
            },
          }, 'Onboarding: complete signup error');
          onError(e);
        });
        break;
      case STEPS.SUCCESS_COMPLETE:
        onContinue();
        navigate(ROUTE_PATH.HOME, { replace: true });
        break;
      default:
        onContinue();
        navigate(ROUTE_PATH.setRegistration(step));
    }
  };

  const handleResend = (type) => {
    const isEmail = type === 'email';
    return dispatch(sendSignupOTP(isEmail ? user.email : '', !isEmail ? `${PHONE_COUNTRY_CODE}${user.phone}` : ''));
  };

  const handleRequestOtp = (type) => {
    return dispatch(getUserOtp(exists.value, type)).then(() => {
      return true;
    }).catch((e) => {
      handleEvent(GA_REGISTRATION_ACTIONS.ERROR, e);
      setError(e);
      return false;
    });
  };

  const props = {
    literals,
    onContinue: handleContinue,
    onBack: handleBack,
    onEvent: handleEvent,
    onError: (e) => {
      handleEvent(GA_REGISTRATION_ACTIONS.ERROR, e);
      setError(e);
    },
  };

  const successStep = [STEPS.SUCCESS_BASIC, STEPS.SUCCESS_BASIC_LINKED, STEPS.SUCCESS_COMPLETE].includes(step);

  // When the gate is off nothing renders: the effect above already
  // redirects to Home.
  if (!registrationEnabled) return null;

  return (
    <Layout className='new-client-layout' bgWhite={!successStep} headerMenu={successStep} rightMenu={false}>
      <AppShell variant='narrow'>
        {step === STEPS.START && (
          <ClientStart
            exists={exists}
            onChange={setExists}
            showNewRegistration={newClientEnabled}
            {...props}
          />
        )}
        {step === STEPS.BASIC_INFO && (
          <ClientBasicInfo
            user={user}
            onChange={setUser}
            {...props}
          />
        )}
        {step === STEPS.EXISTS_OTP_VALIDATION && (
          <ClientExistsOTPValidation
            exists={exists}
            onRequestOtp={handleRequestOtp}
            {...props}
          />
        )}
        {step === STEPS.OTP_VALIDATION && (
          <ClientOTPValidation
            user={user}
            onResend={handleResend}
            {...props}
          />
        )}
        {step === STEPS.PASSWORD && (<ClientPassword customer={user} errorEmail={errorEmailPassword} {...props} />)}
        {step === STEPS.SUCCESS_BASIC && (<ClientSuccessMessage type={SUCCESS_TYPES.BASIC} customer={user} {...props} />)}
        {step === STEPS.SUCCESS_BASIC_LINKED && (<ClientSuccessMessage type={SUCCESS_TYPES.BASIC_LINKED} customer={user} {...props} />)}
        {step === STEPS.IDENTITY_VALIDATION && <ClientIdentityValidation {...props} />}
        {step === STEPS.IDENTITY_RESUME && (
          <ClientIdentityResume
            data={userExtra}
            onChange={setUserExtra}
            {...props}
          />
        )}
        {step === STEPS.ADDRESS && (
          <ClientAddress
            data={userExtra}
            {...props}
          />
        )}
        {step === STEPS.SUCCESS_COMPLETE && <ClientSuccessMessage type={SUCCESS_TYPES.COMPLETE} customer={sessionUser} {...props} />}
        {error && (
          <ErrorModal description={error} onClose={() => setError(null)}>
            {error?.data?.error && ERROR_MESSAGES[error?.data?.error] && (
              <>
                {[ERROR_MESSAGES.EMAIL_ALREADY_EXISTS_IN_SAP, ERROR_MESSAGES.PHONE_ALREADY_EXISTS_IN_SAP].includes(error?.data?.error) ? (
                  <Button
                    onClick={() => {
                      navigate(ROUTE_PATH.setRegistration(STEPS.START));
                      setError(null);
                    }}
                    className='mt-6'
                  >
                    {literals.goToLinkAccount}
                  </Button>
                ) : (
                  <>
                    {ERROR_MESSAGES[error?.data?.error] === ERROR_MESSAGES.INVALID_PASSWORD_TOKEN ? (
                      <Button to={ROUTE_PATH.RECOVER_PASSWORD} className='mt-6'>{literals.goToRecoverPassword}</Button>
                    ) : (
                      <Button to={ROUTE_PATH.LOGIN} className='mt-6'>{literals.goToLogin}</Button>
                    )}
                  </>
                )}
                <Button
                  type='outline-primary'
                  className='mt-2'
                  onClick={() => setError(null)}
                >
                  {literals.common.close}
                </Button>
              </>
            )}
          </ErrorModal>
        )}
      </AppShell>
    </Layout>
  );
};

export default Registration;
